import { classifyRankTitle, locateRankTitlebar, type RankCaptureAssessment } from '@/core/rank-capture'
import { drawSourceToImageData, prepareOcrImage } from '@/core/image'
import type { OcrEngine } from './ocr-engine'

/** One optional inspection per selected source; never owns capture or statistics recovery. */
export class RankCaptureCheck {
  private current: RankCaptureAssessment | null = null
  private attempted = false
  constructor(private readonly onChange: () => void = () => {}) {}
  get assessment(): RankCaptureAssessment | null { return this.current }

  reset(): void { this.current = null; this.attempted = false }

  connect(selectionId: number, displaySurface?: string): void {
    if (this.current?.selectionId === selectionId) return
    this.current = { selectionId, displaySurface, title: 'pending' }
    this.attempted = false
    this.onChange()
  }

  inspect(frame: ImageBitmap | ImageData | HTMLVideoElement, width: number, height: number, engine: OcrEngine): void {
    const source = this.current
    if (!source || source.displaySurface !== 'window' || this.attempted || engine.busy || width <= 0 || height <= 0) return
    this.attempted = true
    const finish = (title: RankCaptureAssessment['title']) => {
      if (this.current !== source) return
      this.current = { ...source, title }
      this.onChange()
    }
    try {
      // Own the crop before the normal pipeline closes its frame bitmap.
      const pixels = drawSourceToImageData(frame, width, height)
      const rect = locateRankTitlebar(pixels)
      if (!rect) { finish('no-titlebar'); return }
      const crop = prepareOcrImage(pixels, width, height, rect, undefined, {
        scale: 3, padding: 0, contrast: 1.15, sharpen: 0, interpolation: 'smooth',
      }).processed
      void engine.recognizeAuxiliary(crop, { textDetLimitSideLen: Math.max(crop.width, crop.height), textRecScoreThresh: .35 })
        .then(reading => {
          if (this.current !== source) return
          const title = classifyRankTitle(reading, crop.width, crop.height)
          if (reading?.text.trim()) console.info('[RankCaptureTitle]', {
            text: reading.text, confidence: reading.confidence, result: title,
          })
          finish(title)
        }).catch(() => finish('unknown'))
    } catch { finish('unknown') }
  }
}
